import { moduleKeys } from 'utils/permissions';
import { guestLikeRoles, userRoles } from 'utils/constants';
import { buildResiPermissionSet } from 'permissions/buildResiPermissionSet';
import { getPropertyAccessLevel, PropertyAccessLevel } from './propertyAccessLevel';
import { indexBy } from './accessNormalize';

export function buildResiAccessScopes({ user, orgs = [], userProperties = [] }) {
    const orgsById = indexBy(orgs, org => org?.org_id);
    const propertyMetadataById = indexBy(userProperties, property => property?.id);

    const membershipProperties = Object.values(user?.properties || {}).filter(Boolean);

    return membershipProperties
        .map(membershipProperty => {
            const propertyId = String(membershipProperty?.id || '');
            const orgId = String(membershipProperty?.org_id || '');

            if (!propertyId || !orgId) {
                return null;
            }

            const org = orgsById[orgId] || null;

            if (!org) {
                return null;
            }

            const propertyMetadata = propertyMetadataById[propertyId] || null;

            const userProperty = {
                ...propertyMetadata,
                ...membershipProperty,

                address: membershipProperty?.address || propertyMetadata?.address || null,

                address_1:
                    propertyMetadata?.address_1 ||
                    membershipProperty?.address?.address_1 ||
                    '',

                full_address:
                    propertyMetadata?.full_address ||
                    membershipProperty?.address?.full_address ||
                    ''
            };

            const accessLevel = getPropertyAccessLevel({
                user,
                org,
                userProperty
            });

            const isMinor = accessLevel === PropertyAccessLevel.MINOR;
            const isLimited = accessLevel === PropertyAccessLevel.LIMITED;
            const isGated = accessLevel === PropertyAccessLevel.GATED;
            const isUnrestricted = accessLevel === PropertyAccessLevel.UNRESTRICTED;

            const hasMyVehiclesModule =
                org?.modules?.includes(moduleKeys.MY_VEHICLES) ||
                org?.my_vehicles === true;

            const hasShortTermRentalModule = org?.modules?.includes(
                moduleKeys.SHORT_TERM_RENTAL
            );

            const role = userProperty?.role;

            const isResident = role === userRoles.RESIDENT || role === 'resi';
            const isTenant = role === userRoles.TENANT || role === 'tenant';
            // The backend assigns `type: LONG_TERM` to every rental tenant regardless of
            // short/long term (a Rental Manager booking still gets `type: LONG_TERM`), so
            // `type` cannot distinguish them. `rental_id` presence is the only reliable
            // short-term (Rental Manager) signal — matches the backend's own
            // `isUserPropertyShortTermTenant` check.
            const isTenantLongTerm = isTenant && !userProperty?.rental_id;
            const isGuest = guestLikeRoles.includes(role);

            const permissions = buildResiPermissionSet({
                isResident,
                isRental: userProperty?.rental === true,
                isPrimary: userProperty?.primary === true,
                isMinor,
                isSuspended: userProperty?.suspended === true,
                canInvite: userProperty?.permissions?.invite === true,
                isTenant,
                isTenantLongTerm,
                isGuest,
                hasMyVehiclesModule,
                hasShortTermRentalModule,
                isOwnerManaged: userProperty?.owner_managed === true,
                isLimited,
                isGated
            });

            return {
                propertyId,
                orgId,

                userProperty,
                propertyMetadata,
                org,

                accessLevel,
                isMinor,
                isLimited,
                isGated,
                isUnrestricted,

                permissions
            };
        })
        .filter(Boolean);
}
